import React, { useContext } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';

import { Role } from './utils';

import { routingMap } from './routing-map';

import { RouterContext } from '../context/routing';
import { ErrorBoundary } from '../sentry';

export type RolesFlag = `*`;
export type Fallback = (roles: Role[]) => string;

export type Roles = {
  or?: Role[];
  and?: Role[];
  flag?: RolesFlag;
};

export type RouteConfig<T extends string> = {
  key: T;
  roles: Roles;
  fallback: Fallback;
  conds?: CondSet[];
  path: string;
  children?: React.ReactNode;
  exact?: boolean;
};

export type RouterFactoryProps<T extends string> = {
  config: RouteConfig<T>[];
  defaultRedirect?: string;
  notFound?: React.FC;
  /**
   * The URL prefix this factory is mounted under (e.g. `/self-service` for the
   * inner self-service factory). When set, paths are stripped of this prefix
   * before being registered, producing the relative paths that v7 descendant
   * `<Routes>` blocks expect. Omit only at the top-level mount, whose parent
   * matches `/*` and therefore passes the full URL to its descendants.
   */
  basePath?: string;
};

export type Routing = {
  roles: Role[];
  routingMap: typeof routingMap;
  init: boolean;
};

type CondSet = [boolean, () => void];

export type RoleCheckerProps = {
  allowedRoles: Roles;
  fallback: Fallback;
  conds?: CondSet[];
  children: React.ReactNode;
};

export type RoutingUtilCheckRoleT = (args: {
  allowedRoles: Roles;
  givenRoles: Role[];
}) => boolean;

export const routingUtilCheckRole: RoutingUtilCheckRoleT = ({
  allowedRoles,
  givenRoles,
}) => {
  const { and, flag, or } = allowedRoles;

  if (flag === '*') return true;

  if (
    (and?.length
      ? and?.every((role) => givenRoles.indexOf(role) !== -1)
      : true) &&
    (or?.length ? or?.some((role) => givenRoles.indexOf(role) !== -1) : true)
  ) {
    return true;
  }

  return false;
};

export type HookUseRoleCheckerT = () => [
  (allowedRoles: Roles) => boolean,
  Routing,
];

export const useRoleChecker: HookUseRoleCheckerT = () => {
  const routeConfig = useContext(RouterContext);
  const { roles: givenRoles } = routeConfig;

  return [
    (allowedRoles) => routingUtilCheckRole({ givenRoles, allowedRoles }),
    routeConfig,
  ];
};

export const RoleChecker: React.FC<RoleCheckerProps> = ({
  allowedRoles,
  fallback,
  conds,
  children,
}) => {
  conds?.forEach((condSet) => condSet[0] && condSet[1]());
  const [checkRoleFunc, { roles }] = useRoleChecker();

  if (checkRoleFunc(allowedRoles)) return <>{children}</>;

  return <Navigate to={fallback(roles)} replace />;
};

/**
 * Shim around v7's useNavigate that keeps the shape callers expect from v5's
 * useHistory: push/replace/goBack/goForward/location. Accepts string or
 * object-form destinations.
 */
type HistoryLike = {
  pathname?: string;
  search?: string;
  state?: unknown;
};

const normalizeTo = (to: string | HistoryLike) => {
  if (typeof to === 'string') return { path: to, state: undefined };
  return {
    path: { pathname: to.pathname ?? '', search: to.search ?? '' },
    state: to.state,
  };
};

export const useHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return {
    push: (to: string | HistoryLike, state?: unknown) => {
      const n = normalizeTo(to);
      navigate(n.path as $TSFixMe, { state: state ?? n.state });
    },
    replace: (to: string | HistoryLike, state?: unknown) => {
      const n = normalizeTo(to);
      navigate(n.path as $TSFixMe, {
        state: state ?? n.state,
        replace: true,
      });
    },
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    location,
  };
};

const CatchAllRedirect: React.FC<{ defaultRedirect: string }> = ({
  defaultRedirect,
}) => {
  const location = useLocation();
  if (!defaultRedirect || defaultRedirect === location.pathname) return null;
  return <Navigate to={defaultRedirect} replace />;
};

const RouterFactory = <T extends string>({
  config,
  defaultRedirect = ``,
  notFound,
  basePath,
}: RouterFactoryProps<T>): JSX.Element => {
  const NotFound = notFound;

  // A descendant <Routes> (the shape every non-top-level RouterFactory takes)
  // only sees the URL portion left over after the parent's match. Registered
  // routes therefore have to be RELATIVE to that mount — absolute paths that
  // include the parent's prefix (e.g. `/self-service/create/:id`) match against
  // the leftover (e.g. `/create/abc`) and silently fail. `basePath` strips the
  // parent's prefix; `exact: true` collapses to `''` (the v7 way of saying
  // "match the parent mount exactly").
  const trimmedBase = basePath?.replace(/\/$/, '') ?? '';
  const stripBase = (p: string) => {
    if (!trimmedBase) return p;
    if (p === trimmedBase) return '';
    if (p.startsWith(`${trimmedBase}/`)) return p.slice(trimmedBase.length + 1);
    return p;
  };

  if (import.meta.env.DEV) {
    if (trimmedBase) {
      config.forEach(({ path, key }) => {
        if (path !== trimmedBase && !path.startsWith(`${trimmedBase}/`)) {
          console.error(
            `[RouterFactory] route "${key}" path "${path}" does not start with basePath "${trimmedBase}". Descendant <Routes> won't match this route.`
          );
        }
      });
    }
    if (!defaultRedirect && !notFound) {
      console.warn(
        '[RouterFactory] No defaultRedirect or notFound provided. Unmatched URLs will render blank.'
      );
    }
  }

  return (
    <Routes>
      {config.map(({ children, roles, fallback, conds, path, key, exact }) => {
        // v5 routes without `exact` had prefix-match semantics; v6+ is exact
        // by default, so append `/*` to preserve the old behavior. Paths are
        // stripped of `basePath` first so the resulting registration is
        // relative to the parent mount (which is what v7 descendant <Routes>
        // require — see comment above).
        const rel = stripBase(path);
        const v7Path = exact ? '' : `${rel.replace(/\/$/, '')}/*`;
        return (
          <Route
            key={key}
            path={v7Path}
            element={
              <RoleChecker
                allowedRoles={roles}
                fallback={fallback}
                conds={conds}
              >
                <ErrorBoundary>{children}</ErrorBoundary>
              </RoleChecker>
            }
          />
        );
      })}
      {NotFound ? (
        <Route path="*" element={<NotFound />} />
      ) : (
        <Route
          path="*"
          element={<CatchAllRedirect defaultRedirect={defaultRedirect} />}
        />
      )}
    </Routes>
  );
};

const useRouting = () => {
  const routing: Routing = useContext(RouterContext);
  return routing;
};

useRouting.useHistory = useHistory;

export { useRouting };

export default RouterFactory;
